import React, { useEffect, useState, useRef } from 'react';
import './PhaseProgress.css';

/**
 * PhaseProgress — stepped multi-phase progress indicator.
 *
 * Props:
 *   phases: string[]  Labels to display for each phase (one circle per label).
 *   isActive: bool    When true the pipeline advances and animates.
 *   variant?: 'inline' | 'panel'  Layout variant (default 'inline').
 *   phaseDurationMs?: number  How long each non-final phase is shown.
 *
 * The last phase stays active until isActive flips to false, so a slow
 * request never "runs out" of progress to show.
 */
export default function PhaseProgress({
  phases,
  isActive,
  variant = 'inline',
  phaseDurationMs = 1400,
}) {
  const [currentPhase, setCurrentPhase] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      setCurrentPhase(0);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return undefined;
    }

    const advance = () => {
      setCurrentPhase((prev) => {
        if (prev >= phases.length - 1) return prev;
        return prev + 1;
      });
    };

    timeoutRef.current = setTimeout(function tick() {
      advance();
      timeoutRef.current = setTimeout(tick, phaseDurationMs);
    }, phaseDurationMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isActive, phases.length, phaseDurationMs]);

  if (!isActive) return null;

  return (
    <div className={`phase-progress phase-progress--${variant}`} role="status" aria-live="polite">
      <div className="phase-progress__track">
        {phases.map((label, index) => {
          const isCompleted = index < currentPhase;
          const isCurrent = index === currentPhase;
          const stepClass = [
            'phase-progress__step',
            isCompleted ? 'is-completed' : '',
            isCurrent ? 'is-current' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <React.Fragment key={`${label}-${index}`}>
              <div className={stepClass}>
                <div className="phase-progress__circle">
                  {isCompleted ? (
                    <svg
                      viewBox="0 0 24 24"
                      className="phase-progress__check"
                      aria-hidden="true"
                    >
                      <path
                        d="M20.292 5.292a1 1 0 010 1.414l-11 11a1 1 0 01-1.414 0l-5-5a1 1 0 111.414-1.414L9 15.586 18.878 5.708a1 1 0 011.414 0z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <span className="phase-progress__dot" aria-hidden="true" />
                  )}
                </div>
              </div>
              {index < phases.length - 1 && (
                <div
                  className={`phase-progress__connector ${
                    index < currentPhase ? 'is-filled' : ''
                  } ${index === currentPhase ? 'is-active' : ''}`}
                  aria-hidden="true"
                >
                  <span className="phase-progress__connector-fill" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="phase-progress__label" key={`label-${currentPhase}`}>
        {phases[currentPhase]}
      </div>
    </div>
  );
}
